import { Analytics } from 'client/Global/Analytics'
import { GlobalEvents } from 'client/Other/GlobalEvents'
import { debounce } from 'lodash'
import * as React from 'react'

import { TestableInterface } from '../interfaces/TestableInterface'
import { makeTranslatable, TranslatableType } from '../interfaces/Translatable'
import { ValidatableInterface } from '../interfaces/ValidatableInterface'
import { formatRequiredIndicator } from '../Required/Required'
import { RequiredInterface } from '../Required/RequiredInterface'
import { HelpTooltip } from '../Tooltip/Tooltip'
import './SelectInput.scss'

const MAX_ITEMS = 10

export type OpeningDirection = 'up' | 'down'

export interface SelectInputProps extends TestableInterface, ValidatableInterface, RequiredInterface {
  label?: TranslatableType
  value: any | any[]
  map: { [key: string]: string }
  onChange?: (value: string | string[]) => void
  onFocus?: () => void
  onBlur?: (value?: string) => void
  multi?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  selectPlaceholder?: string
  labelPosition?: 'left' | 'top'
  borderless?: boolean
  addNewLabel?: string
  onAddNew?: () => void
  openDirection?: OpeningDirection
  orderBy?: 'key' | 'value'
  sortFn?: () => number
  disabled?: boolean
  tail?: JSX.Element
  customClass?: boolean
  hasTitles?: boolean
  defaultSearchOption?: string
  tooltipText?: string
  origin?: string
}

export interface SelectInputState {
  menuOpen: boolean
  search: string
  index: number
}

export class SelectInput extends React.Component<SelectInputProps, SelectInputState> {
  private isInvoiceType = false
  constructor(props: SelectInputProps) {
    super(props)

    let index = 0
    Object.keys(props.map).forEach((val, i) => {
      if (this.props.value !== null && val === this.props.value.toString()) {
        index = i
      }
    })
    this.state = {
      menuOpen: false,
      search: '',
      index,
    }
  }

  expenseCategoriesDebouncedSearch = debounce((v) => {
    Analytics.trackEvent('searchExpenseCategories', {
      type: 'action',
      category: 'expense',
      result: 'success',
      placement: 'expenses',
      searchedTerm: v,
    })
  }, 1000)

  public static defaultProps: SelectInputProps = {
    value: null,
    map: {},
    multi: false,
    openDirection: undefined,
  }

  // get text value for selected key
  private getSelectedValue(): string {
    // try-catch here because somehow testing 'if (this.props.value && this.props.map[this.props.value])'
    // returns false if value is '0'.
    try {
      if (Array.isArray(this.props.value)) {
        let result: string = ''
        this.props.value.forEach((value) => {
          if (!result) {
            result = this.props.map[value]
          } else {
            result += ', ' + this.props.map[value]
          }
        })
        return result
      } else {
        return this.props.map[this.props.value]
      }
    } catch (e) {
      console.error('Value missing in map')
    }
    return ''
  }

  // just close the list div
  private handleOnBlur() {
    this.setState({ menuOpen: false })
  }

  private e
  private onMouseEnterEnabled = true
  private onlyOption: string | null = null
  private searchInput: HTMLInputElement | null = null

  public componentDidMount() {
    this.e = GlobalEvents.Click((e) => {
      this.handleOnBlur()
    })
  }

  public componentDidUpdate() {
    if (this.searchInput) {
      this.searchInput.focus()
    }
    window.requestAnimationFrame(() => {
      if (this.state.menuOpen) {
        this.checkMenuOpeningDirection()
      }
    })
  }

  public componentWillUnmount() {
    GlobalEvents.unBind(this.e)
  }

  // select value from the list
  private handleSelect(value: string) {
    if (this.props.onChange) {
      this.props.onChange(value)
    }
  }

  private handleClickOnMultiSelect(value: string) {
    const options: string[] = []
    if (!this.props.value) {
      options.push(value)
    } else if (this.props.value.indexOf(value) >= 0) {
      // remove clicked value from array
      this.props.value.forEach((val) => {
        if (val !== value) {
          options.push(val)
        }
      })
    } else {
      // add clicked value to array in its place
      Object.keys(this.props.map).map((key) => {
        if (this.props.value.indexOf(key) >= 0 || key === value) {
          options.push(key)
        }
      })
    }
    if (this.props.onChange) {
      this.props.onChange(options)
    }
  }

  private checkMenuOpeningDirection() {
    // Hackety hack to open menu upwards if it would go over the bottom edge of content div.
    // This is mostly for modals that may be smaller and/or scrollable.
    // Requires a parent div at some level with className 'content'
    const menuDiv = document.getElementById(`menuDiv-${this.props.automation}`)
    if (menuDiv && !this.props.openDirection) {
      let parentDiv = menuDiv.parentElement
      while (parentDiv?.parentElement) {
        parentDiv = parentDiv.parentElement
        if (parentDiv.className.indexOf('content') >= 0) {
          if (menuDiv.getBoundingClientRect().bottom > parentDiv.getBoundingClientRect().bottom) {
            menuDiv.className = 'select-menu up'
          }
          break
        }
      }
    }
  }

  private static scrollSelectedToView(automation) {
    const selected = document.getElementsByClassName(`${automation}-selected`)
    if (selected && selected[0]) {
      selected[0].scrollIntoView({ block: 'nearest', behavior: 'auto' })
    }
  }

  private getOptions() {
    let options: { id?: string; key: string; value: string }[] = []
    Object.keys(this.props.map).forEach((key) => {
      const value = this.props.map[key]
      if (this.state.search === '' || value.toString().toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0) {
        options.push({ key, value })
      }
    })
    if (options.length === 0) {
      const key = this.props.defaultSearchOption || ''
      const value = this.props.map[key]
      options.push({ key, value })
    }
    // order if necessary
    if (this.props.orderBy && !this.props.hasTitles) {
      options = options.sort((a, b) => {
        if (this.props.orderBy && a[this.props.orderBy] < b[this.props.orderBy]) {
          return -1
        }
        if (this.props.orderBy && a[this.props.orderBy] > b[this.props.orderBy]) {
          return 1
        }
        return 0
      })
    }
    if (this.props.sortFn) {
      options.sort(this.props.sortFn)
    }
    if (options.length === 1) {
      this.onlyOption = options[0].key
    } else {
      this.onlyOption = null
    }
    return options
  }

  private openMenu() {
    let index = 0
    const options = this.getOptions()
    this.onMouseEnterEnabled = false
    options.forEach((val, i) => {
      if (this.props.value !== null && val.key === this.props.value.toString()) {
        index = i
      }
    })
    this.setState({
      menuOpen: true,
      index,
    })
    this.checkMenuOpeningDirection()
    setTimeout(() => SelectInput.scrollSelectedToView(this.props.automation), 100)
  }

  private onKeyDown(e) {
    switch (e.keyCode) {
      case 9: // must catch tab here as on keyUp it doesn't work so well
        this.setState({ search: '', menuOpen: false })
        break
      case 38: // prevent up/down arrows from scrolling the page
      case 40:
        e.preventDefault()
    }
  }

  // handle closing the list when user presses enter, tab and esc
  // and changing selection with arrows and selecting with enter
  private onKeyUp(e) {
    const numberOfResults = Object.keys(this.props.map).length
    switch (e.keyCode) {
      case 9: // tab
      case 27: // esc
        if (this.state.search !== '') {
          this.setState({ search: '', menuOpen: false })
        } else {
          this.setState({ menuOpen: false })
        }
        break
      case 13: // enter
        if (this.state.menuOpen) {
          if (this.onlyOption) {
            if (this.props.onChange) {
              this.props.onChange(this.onlyOption)
            }
            this.setState({
              search: '',
              menuOpen: false,
            })
          } else {
            let selected: string = ''
            const options = this.getOptions()
            options.forEach((val, i) => {
              if (i === this.state.index) {
                selected = val.key
              }
            })
            this.setState({ menuOpen: false })
            if (this.props.onChange) {
              this.props.onChange(selected)
            }
          }
        } else {
          this.openMenu()
        }
        break
      case 38: // up
        if (this.state.menuOpen) {
          if (this.state.index > 0) {
            this.setState({ index: this.state.index - 1 })
          } else {
            this.setState({ index: numberOfResults - 1 })
          }
          this.onMouseEnterEnabled = false
          setTimeout(() => SelectInput.scrollSelectedToView(this.props.automation), 100)
        } else {
          this.openMenu()
        }
        e.preventDefault()
        break
      case 40: // down
        if (this.state.menuOpen) {
          if (this.state.index >= 0 && this.state.index < numberOfResults - 1) {
            this.setState({ index: this.state.index + 1 })
          } else {
            this.setState({ index: 0 })
          }
          this.onMouseEnterEnabled = false
          setTimeout(() => SelectInput.scrollSelectedToView(this.props.automation), 100)
        } else {
          this.openMenu()
        }
        e.preventDefault()
        break
    }
  }

  private renderSelectMenu() {
    const result: React.ReactElement<HTMLDivElement, any>[] = []
    const self = this

    if (this.props.searchable && Object.keys(this.props.map).length > MAX_ITEMS) {
      result.push(
        <div
          className="search"
          key="search"
          onClick={() => {
            GlobalEvents.prevent(this.e)
            return false
          }}
        >
          <input
            type="text"
            ref={(input) => {
              this.searchInput = input
            }}
            data-automation={this.props.automation + '-search'}
            value={this.state.search}
            onKeyUp={this.onKeyUp.bind(this)}
            onKeyDown={(e) => this.onKeyDown(e)}
            onChange={(e) => {
              this.setState({ search: e.currentTarget.value })
              if (this.props.origin === 'expense-categories') {
                this.expenseCategoriesDebouncedSearch(e.currentTarget.value)
              }
            }}
            placeholder={this.props.searchPlaceholder}
          />
          <span
            className="clear"
            data-automation={this.props.automation + '-clear'}
            onClick={() => {
              this.setState({ search: '' })
            }}
          >
            x
          </span>
        </div>,
      )
    }

    let options = this.getOptions()
    // CORE-3285 make the vat exemption the third item
    // We have to do this because the types are sorted by id
    const vatlessId = '127'
    if (this.isInvoiceType && options.find((o) => o.id === vatlessId)) {
      options = options.filter((o) => o.id !== vatlessId)
      options.splice(2, 0, vatlessId)
    }
    options.forEach((o, index) => {
      if (self.props.multi) {
        let checked = false
        if (self.props.value && self.props.value.indexOf(o.key) > -1) {
          checked = true
        }
        result.push(
          <div
            role="menuitemcheckbox"
            className="option"
            key={o.key}
            value={o.key}
            onClick={(e) => {
              e.stopPropagation()
              self.handleClickOnMultiSelect(o.key)
            }}
          >
            <input type="checkbox" checked={checked} onChange={() => self.handleClickOnMultiSelect(o.key)} />
            {o.value}
          </div>,
        )
      } else {
        if (this.props.hasTitles && o.key.includes('title')) {
          result.push(
            <div key={o.key} value={o.key} role="menuitem" className={'option title'}>
              {o.value}
            </div>,
          )
        } else {
          result.push(
            <div
              key={o.key}
              value={o.key}
              role="menuitem"
              className={
                'option' +
                (self.props.customClass ? ` ${o.key}` : '') +
                (index === this.state.index ? ` active ${this.props.automation}-selected` : '')
              }
              // onClick={self.handleSelect.bind(self)}
              onClick={() => self.handleSelect(o.key)}
              onMouseEnter={() => {
                if (this.onMouseEnterEnabled) {
                  this.setState({ index })
                } else {
                  this.onMouseEnterEnabled = true
                }
              }}
            >
              {o.value}
            </div>,
          )
        }
      }
    })

    return (
      <div role="menu" className="optionsHolder">
        {result}
      </div>
    )
  }

  private renderActionMenu() {
    const { addNewLabel, onAddNew } = this.props

    if ((addNewLabel && !onAddNew) || (!addNewLabel && onAddNew)) {
      throw new Error(
        'Invalid prop "addNewLabel|onAddNew" supplied to SelectInput. Validation failed, please provide both',
      )
    }

    if (addNewLabel && onAddNew) {
      return (
        <div className="action-btn" onClick={onAddNew}>
          {this.props.addNewLabel}
        </div>
      )
    }

    return null
  }

  public render(): JSX.Element {
    const classes = ['SelectInput', 'Controlled', 'Input']
    const required = formatRequiredIndicator(this.props)
    if (this.props.labelPosition && this.props.labelPosition === 'top') {
      classes.push('LabelOnTop')
    }
    if (this.props.borderless) {
      classes.push('Borderless')
    }
    if (this.props.invalid) {
      classes.push('invalid')
    }
    if (this.props.disabled) {
      classes.push('SelectInput--disabled')
    }
    let label: React.ReactElement<HTMLDivElement, any> | null = null
    this.isInvoiceType = this.props.automation ? this.props.automation.includes('invoice-type') : false
    if (this.props.label) {
      label = (
        <span
          data-automation={this.props.automation + '-label'}
          className={`Label ${this.props.tooltipText ? 'with-tooltip' : ''}`}
        >
          {makeTranslatable(this.props.label)}
          {required}
          {this.props.tooltipText && (
            <HelpTooltip
              placement="auto"
              header={{
                body: <span dangerouslySetInnerHTML={{ __html: this.props.tooltipText }} />,
              }}
            />
          )}
        </span>
      )
    }

    let menu: React.ReactElement<HTMLDivElement, any> | null = null
    if (this.state.menuOpen) {
      const direction = this.props.openDirection || ''
      menu = (
        <div className={`select-menu ${direction}`} id={`menuDiv-${this.props.automation || ''}`}>
          {this.renderSelectMenu()}
          {this.renderActionMenu()}
        </div>
      )
      classes.push('menuOpen')
    }

    this.checkMenuOpeningDirection()

    return (
      <div
        className={classes.join(' ')}
        data-automation={this.props.automation}
        value={this.props.value}
        data-value={this.props.value}
        onBlur={() => {
          if (this.props.onBlur) {
            this.props.onBlur()
          }
        }}
      >
        {label}
        <div className="select Controlled">
          <div
            role="button"
            className="selected-value"
            onClick={(e) => {
              if (this.props.disabled) {
                return false
              }

              if (!this.state.menuOpen) {
                this.openMenu()
              } else {
                this.setState({ menuOpen: false })
              }

              GlobalEvents.prevent(this.e)
              return false
            }}
            tabIndex={0}
            onFocus={() => {
              if (this.props.onFocus) {
                this.props.onFocus()
              }
            }}
            onKeyDown={(e) => this.onKeyDown(e)}
            onKeyUp={(e) => this.onKeyUp(e)}
          >
            <span className="selected-value">
              {this.props.value !== null && this.getSelectedValue()}
              {!this.props.value && this.props.selectPlaceholder && (
                <span className="placeholder">{this.props.selectPlaceholder}</span>
              )}
            </span>
            <span className="arrow" />
          </div>
          {menu}
        </div>
        {this.props.tail ? this.props.tail : null}
      </div>
    )
  }
}
